// Auth context — Phase 7. Supports two parallel session types:
//   - User session (email + password) via /api/auth/* — primary
//   - Admin session (legacy single password) via /api/admin/* — kept for ops
// The state exposes whichever is active. Routes can use `useAuth()` and
// redirect based on state.status.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, userAuth, ApiError, type PublicUser } from './api.js';

type AuthState =
  | { status: 'unknown' }
  | { status: 'authed'; kind: 'user'; user: PublicUser }
  | { status: 'authed'; kind: 'admin' }
  | { status: 'unauthed' };

interface AuthValue {
  state: AuthState;
  signupUser: (email: string, password: string, displayName?: string) => Promise<void>;
  loginUser: (email: string, password: string) => Promise<void>;
  loginAdmin: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  recheck: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'unknown' });

  const recheck = async () => {
    // Try user session first
    try {
      const me = await userAuth.me();
      setState({ status: 'authed', kind: 'user', user: me.user });
      return;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Network / server error — drop to unauthed
        setState({ status: 'unauthed' });
        return;
      }
    }
    // Fall back to admin session
    try {
      await auth.me();
      setState({ status: 'authed', kind: 'admin' });
    } catch {
      setState({ status: 'unauthed' });
    }
  };

  useEffect(() => {
    void recheck();
  }, []);

  const value: AuthValue = {
    state,
    signupUser: async (email, password, displayName) => {
      const r = await userAuth.signup(email, password, displayName);
      setState({ status: 'authed', kind: 'user', user: r.user });
    },
    loginUser: async (email, password) => {
      const r = await userAuth.login(email, password);
      setState({ status: 'authed', kind: 'user', user: r.user });
    },
    loginAdmin: async (password) => {
      await auth.login(password);
      setState({ status: 'authed', kind: 'admin' });
    },
    logout: async () => {
      // Try logging out of whichever session is active. Both endpoints
      // are idempotent so it's safe to call both.
      try {
        await userAuth.logout();
      } catch {}
      try {
        await auth.logout();
      } catch {}
      setState({ status: 'unauthed' });
    },
    recheck,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth: missing AuthProvider');
  return v;
}
